import React, { useState } from "react";
import Layout from "../components/Layout";
import { API } from "../constants";

/**
 * Public Subscriber's Manual viewer at /manual.
 * No authentication gate. PDF is embedded inline; a Download button serves the
 * same file with Content-Disposition: attachment.
 */
export default function ManualPage() {
  const [adminKey, setAdminKey] = useState("");
  const [adminError, setAdminError] = useState("");

  const subscriberView = `${API}/manuals/subscriber`;
  const subscriberDownload = `${API}/manuals/subscriber?download=true`;
  const adminView = adminKey
    ? `${API}/manuals/administrator?key=${encodeURIComponent(adminKey)}`
    : null;
  const adminDownload = adminKey
    ? `${API}/manuals/administrator?key=${encodeURIComponent(adminKey)}&download=true`
    : null;

  const validateAdmin = async (e) => {
    e.preventDefault();
    setAdminError("");
    try {
      const res = await fetch(`${API}/manuals/administrator?key=${encodeURIComponent(adminKey)}`);
      if (res.status === 401) {
        setAdminError("Invalid administrator password");
        setAdminKey("");
      } else if (!res.ok) {
        setAdminError(`Unexpected error (${res.status})`);
      }
    } catch (err) {
      setAdminError(err.message);
    }
  };

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 text-center">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-3" data-testid="manual-title">
              Top Screen™ Manuals
            </h1>
            <p className="text-[#E8E4DC] max-w-2xl mx-auto">
              The complete book-formatted PDF manuals — every feature, every shortcut, every setting.
            </p>
          </div>

          {/* Subscriber Manual */}
          <section className="card-glass p-6 mb-8" data-testid="subscriber-manual-card">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
              <div>
                <span className="inline-block text-[10px] font-mono px-2 py-0.5 rounded bg-[#A3E635]/15 text-[#A3E635] mb-2">
                  PUBLIC · v1.0
                </span>
                <h2 className="font-heading text-2xl font-bold text-white">
                  The Subscriber's Manual
                </h2>
                <p className="text-[#E8E4DC] text-sm">
                  Every Feature, Every Setting, Every Shortcut · 16 chapters · indexed
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <a
                  href={subscriberView}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-sm px-4 py-2"
                  data-testid="subscriber-manual-view"
                >
                  Open in new tab
                </a>
                <a
                  href={subscriberDownload}
                  className="btn-primary text-sm px-4 py-2"
                  data-testid="subscriber-manual-download"
                >
                  Download PDF
                </a>
              </div>
            </div>
            <div
              className="bg-black/40 rounded-lg overflow-hidden border border-white/10"
              style={{ height: "78vh" }}
              data-testid="subscriber-manual-embed"
            >
              <iframe
                src={subscriberView}
                title="Top Screen Subscriber's Manual"
                className="w-full h-full"
                style={{ border: 0 }}
              />
            </div>
          </section>

          {/* Administrator Manual */}
          <section className="card-glass p-6" data-testid="administrator-manual-card">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
              <div>
                <span className="inline-block text-[10px] font-mono px-2 py-0.5 rounded bg-red-500/15 text-red-400 mb-2">
                  ADMIN · v1.0
                </span>
                <h2 className="font-heading text-2xl font-bold text-white">
                  The Administrator's Manual
                </h2>
                <p className="text-[#E8E4DC] text-sm">
                  Subscriber chapters + 12 administrator-only chapters · 28 total · indexed
                </p>
              </div>
              {adminKey && (
                <div className="flex gap-2 flex-shrink-0">
                  <a
                    href={adminView}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary text-sm px-4 py-2"
                    data-testid="administrator-manual-view"
                  >
                    Open in new tab
                  </a>
                  <a
                    href={adminDownload}
                    className="btn-primary text-sm px-4 py-2"
                    data-testid="administrator-manual-download"
                  >
                    Download PDF
                  </a>
                </div>
              )}
            </div>
            {!adminKey ? (
              <form onSubmit={validateAdmin} className="max-w-md">
                <p className="text-[#E8E4DC] text-sm mb-3">
                  Enter administrator password to view or download.
                </p>
                <div className="flex gap-2">
                  <input
                    type="password"
                    value={adminKey}
                    onChange={(e) => setAdminKey(e.target.value)}
                    placeholder="Administrator password"
                    className="input-dark flex-1 text-center"
                    data-testid="administrator-manual-password"
                  />
                  <button
                    type="submit"
                    className="btn-primary px-5"
                    data-testid="administrator-manual-submit"
                  >
                    Unlock
                  </button>
                </div>
                {adminError && (
                  <p className="text-red-400 text-xs mt-2" data-testid="administrator-manual-error">
                    {adminError}
                  </p>
                )}
              </form>
            ) : (
              <div
                className="bg-black/40 rounded-lg overflow-hidden border border-white/10"
                style={{ height: "78vh" }}
                data-testid="administrator-manual-embed"
              >
                <iframe
                  src={adminView}
                  title="Top Screen Administrator's Manual"
                  className="w-full h-full"
                  style={{ border: 0 }}
                />
              </div>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}
